import React, { useState, useEffect, useRef, useCallback } from 'react';
import { manualMeta, manualSections } from './manualData';
import './manual.css';

const noteIcon = {
    note: 'bx bx-info-circle',
    tip: 'bx bx-bulb',
    important: 'bx bx-error',
};

const Block = ({ block }) => {
    switch (block.type) {
        case 'p':
            return <p className="manual-p">{block.text}</p>;
        case 'steps':
            return (
                <div className="manual-steps-wrap">
                    {block.title && <h3 className="manual-sub">{block.title}</h3>}
                    <ol className="manual-steps">
                        {block.items.map((it, i) => (
                            <li key={i}>{it}</li>
                        ))}
                    </ol>
                </div>
            );
        case 'note':
            return (
                <div className={`manual-note manual-note-${block.variant}`}>
                    <i className={noteIcon[block.variant] || noteIcon.note} />
                    <span>{block.text}</span>
                </div>
            );
        case 'image':
            return (
                <figure className="manual-figure">
                    <img src={block.src} alt={block.caption} loading="lazy" />
                    {block.caption && <figcaption>{block.caption}</figcaption>}
                </figure>
            );
        case 'table':
            return (
                <div className="manual-table-wrap">
                    {block.title && <h3 className="manual-sub">{block.title}</h3>}
                    <table className="manual-table">
                        <thead>
                            <tr>
                                {block.head.map((h, i) => (
                                    <th key={i}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {block.rows.map((row, ri) => (
                                <tr key={ri}>
                                    {row.map((cell, ci) => (
                                        <td key={ci}>{cell}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        default:
            return null;
    }
};

const Manual = () => {
    const [activeId, setActiveId] = useState(manualSections[0].id);
    const [query, setQuery] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const contentRef = useRef(null);
    const sectionRefs = useRef({});

    // Scrollspy: highlight the section currently in view.
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible.length > 0) {
                    setActiveId(visible[0].target.id);
                }
            },
            { rootMargin: '-90px 0px -70% 0px', threshold: 0 }
        );
        const nodes = Object.values(sectionRefs.current).filter(Boolean);
        nodes.forEach((n) => observer.observe(n));
        return () => observer.disconnect();
    }, []);

    const scrollTo = useCallback((id) => {
        const el = sectionRefs.current[id];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveId(id);
            setSidebarOpen(false);
        }
    }, []);

    const filtered = query.trim()
        ? manualSections.filter((s) => s.title.toLowerCase().includes(query.trim().toLowerCase()))
        : manualSections;

    return (
        <div className="page-content manual-page">
            <div className="manual-shell">
                {/* Sidebar / Index */}
                <aside className={`manual-sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="manual-sidebar-inner">
                        <div className="manual-brand">
                            <span className="manual-brand-title">{manualMeta.title}</span>
                            <span className="manual-brand-ver">v{manualMeta.version}</span>
                        </div>
                        <div className="manual-search">
                            <i className="bx bx-search" />
                            <input
                                type="text"
                                placeholder="Search sections..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </div>
                        <nav className="manual-toc">
                            {filtered.map((s) => (
                                <button
                                    key={s.id}
                                    className={`manual-toc-item ${activeId === s.id ? 'active' : ''}`}
                                    onClick={() => scrollTo(s.id)}
                                    type="button"
                                >
                                    <span className="manual-toc-num">{s.number}</span>
                                    <span className="manual-toc-text">{s.title}</span>
                                </button>
                            ))}
                            {filtered.length === 0 && <div className="manual-toc-empty">No matches</div>}
                        </nav>
                    </div>
                </aside>

                {/* Main content */}
                <main className="manual-main" ref={contentRef}>
                    <button
                        type="button"
                        className="manual-menu-toggle"
                        onClick={() => setSidebarOpen((v) => !v)}
                    >
                        <i className="bx bx-menu" /> Contents
                    </button>

                    <header className="manual-hero">
                        <h1>{manualMeta.title}</h1>
                        <p className="manual-hero-sub">{manualMeta.subtitle}</p>
                        <p className="manual-hero-intro">{manualMeta.intro}</p>
                        <div className="manual-meta-grid">
                            <div>
                                <span className="manual-meta-label">Organization</span>
                                <span className="manual-meta-value">{manualMeta.organization}</span>
                            </div>
                            <div>
                                <span className="manual-meta-label">Version</span>
                                <span className="manual-meta-value">{manualMeta.version}</span>
                            </div>
                            <div>
                                <span className="manual-meta-label">Last Updated</span>
                                <span className="manual-meta-value">{manualMeta.lastUpdated}</span>
                            </div>
                            <div>
                                <span className="manual-meta-label">Classification</span>
                                <span className="manual-meta-value">{manualMeta.classification}</span>
                            </div>
                        </div>
                        <div className="manual-audience">
                            {manualMeta.intendedFor.map((a) => (
                                <span key={a} className="manual-chip">
                                    {a}
                                </span>
                            ))}
                        </div>
                    </header>

                    {manualSections.map((section) => (
                        <section
                            key={section.id}
                            id={section.id}
                            className="manual-section"
                            ref={(el) => {
                                sectionRefs.current[section.id] = el;
                            }}
                        >
                            <h2 className="manual-section-title">
                                <span className="manual-section-num">{section.number}</span>
                                {section.title}
                            </h2>
                            {section.blocks.map((block, i) => (
                                <Block key={i} block={block} />
                            ))}
                        </section>
                    ))}

                    <footer className="manual-footer">
                        BlueyardIQ User Manual · Confidential · v{manualMeta.version} · {manualMeta.lastUpdated}
                        <br />
                        Need assistance? Contact +91 79820 68646 or email support@ishipplus.com
                    </footer>
                </main>
            </div>
        </div>
    );
};

export default Manual;
